/**
 * The dashboard talks to the same /v1 API developers use, signed in with the
 * session cookie instead of an API key (apps/api/src/accounts).
 */
const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...rest,
      headers: { ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach JustOnAir. Check your connection.');
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, body?.error?.code ?? 'http_error', body?.error?.message ?? res.statusText);
  }
  return body as T;
}

export interface Account {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'active' | 'suspended';
  trust_tier: 'new' | 'trusted' | 'partner';
  /** prepaid: stops at zero. postpaid (by agreement) and internal (our own products): may go below zero. */
  billing_mode: 'prepaid' | 'postpaid' | 'internal';
  recording_retention_days: number | null;
  notifications: NotificationSettings;
  created_at: string;
}

/** Which emails the account gets before its credit runs out (GET/PATCH /v1/account/notifications). */
export interface NotificationSettings {
  low_balance_email: boolean;
  /** Dollars, "5.00". Also the threshold of the credit.low webhook. */
  low_balance_threshold_usd: string;
  recording_deletion_email: boolean;
  /** Postpaid only: balance-owed emails. */
  balance_statement_email: boolean;
}

export interface Me {
  user: { id: string; email: string; name: string | null };
  account: Account;
  accounts: (Account & { role: string })[];
  legal: Legal;
}

/** The Terms of Service, Acceptable Use Policy and DPA, and whether this account accepted the current version. */
export interface Legal {
  version: string;
  accepted: boolean;
  documents: { terms: string; aup: string; dpa: string };
  privacy: string;
}

export interface Recording {
  status: string;
  bytes: number | null;
  duration_seconds: number | null;
  retention_days: number | null;
  ready_at: string | null;
  delete_at: string | null;
  delete_reason: 'retention' | 'zero_balance' | null;
  deleted_at: string | null;
}

/** GET /v1/streams/:id only: what this stream has cost so far, at today's rates. */
export interface StreamCost {
  total_usd: string;
  delivery_bytes: number;
  delivery_usd: string;
  /** Delivery is read from the CDN's logs an hour at a time; counted up to here. Null when not metered yet. */
  delivery_counted_until: string | null;
  /** Encoding minutes (adaptive) or passthrough minutes, as seconds. */
  ingest_seconds: number;
  ingest_usd: string;
  storage_gb_hours: number;
  storage_usd: string;
}

export interface Stream {
  id: string;
  name: string | null;
  /** The channel that started this stream; null for streams created directly. */
  channel_id?: string | null;
  status: 'pending' | 'live' | 'ended' | 'expired' | 'cancelled';
  profile: 'abr_basic' | 'passthrough';
  record: boolean;
  /** After the end, the hosted player plays the recording. */
  replay?: boolean;
  recording: Recording | null;
  node_ready: boolean;
  rtmp_url: string | null;
  playback_url: string | null;
  playback_url_expires_at: string | null;
  embed_url: string;
  renditions: string[];
  max_ingest_resolution: number;
  max_duration_seconds: number;
  metadata: Record<string, unknown> | null;
  /** How the hosted player presents the stream. */
  player?: { enabled?: boolean; show_name: boolean; require_viewer_token?: boolean; max_height?: number | null; watch_minutes_limit?: number | null; closed_at?: string | null; allowed_domains?: string[] };
  /** A frame a minute from the ingest node. `url` is for the owner; `public` opens it to the player and public_url. */
  thumbnail?: { public: boolean; url: string | null; captured_at: string | null; public_url: string | null };
  /** Viewer chat and reactions (beta). */
  chat?: ChatSettings;
  reservation_expires_at: string | null;
  /** Seconds left when the response was made (so a wrong local clock doesn't matter). */
  reservation_expires_in_seconds?: number | null;
  created_at: string;
  went_live_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  ingest: {
    width: number | null;
    height: number | null;
    kbps: number | null;
    observed_at: string;
    limit_warning: string | null;
    keyframe_interval_seconds?: number | null;
    keyframe_warning?: boolean;
  } | null;
  cost?: StreamCost;
  viewers?: StreamViewers;
  /** Watching on the hosted player right now (estimate); null when not live or nobody is on it. */
  viewers_now?: number | null;
}

/** A stable address and persistent key; each connect of the key starts a new Stream. */
export interface Channel {
  id: string;
  object: 'channel';
  name: string | null;
  status: 'active' | 'disabled';
  profile: 'abr_basic' | 'passthrough';
  rtmp_url: string;
  embed_url: string;
  live_stream_id: string | null;
  scheduled_at: string | null;
  defaults: {
    max_resolution?: number;
    record?: boolean;
    replay?: boolean;
    recording_retention_days?: number;
    metadata?: Record<string, unknown>;
    player?: Record<string, unknown>;
    thumbnail?: { public?: boolean };
    chat?: { enabled?: boolean; reactions?: boolean };
  };
  stream_key_rotated_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Returned by POST /v1/channels and the key rotation only. */
export interface CreatedChannel extends Channel {
  stream_key: string;
  whip_url: string;
}

/** Channel keys from create/rotate, kept in memory until the user confirms they saved them. */
export const channelSecrets = new Map<string, Pick<CreatedChannel, 'stream_key' | 'whip_url'>>();

/** Returned by POST /v1/streams only: the secrets, never shown again. */
export interface CreatedStream extends Stream {
  stream_key: string;
  whip_url: string;
}

export interface UsageDay {
  date: string;
  delivery_bytes: number;
  transcode_seconds: number;
  passthrough_seconds: number;
  storage_gb_hours: number;
  /** Minutes watched by all viewers that day, from the CDN logs. */
  watch_minutes?: number;
  cost_usd: string;
  /** Internal accounts only: what the same usage cost JustOnAir. */
  our_cost_usd?: string;
  charged: boolean;
}

export interface Usage {
  billing_mode: 'prepaid' | 'postpaid' | 'internal';
  our_cost_rates?: { delivery_usd_per_gb: string; storage_usd_per_gb_month: string; fixed_eur_per_month: string };
  balance_usd: string;
  available_usd: string;
  balance_depleted_at: string | null;
  recordings_delete_at: string | null;
  rates: {
    delivery_usd_per_gb: number;
    transcode_usd_per_minute: number;
    passthrough_usd_per_minute: number;
    storage_usd_per_gb_month: number;
  };
  days: UsageDay[];
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string | null;
  events: string[];
  all_events: boolean;
  enabled: boolean;
  created_at: string;
  /** Only on create, GET one, and rotation. */
  secret?: string;
}

export interface WebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  stream_id: string | null;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  max_attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_status_code: number | null;
  last_error: string | null;
  last_duration_ms: number | null;
  delivered_at: string | null;
  created_at: string;
  payload: unknown;
}

/** One viewer known by a viewer token, and how long they watched (GET /v1/streams/:id/attendance). */
export interface Attendance {
  object: 'attendance';
  viewer_id: string;
  name: string | null;
  watch_seconds: number;
  first_seen_at: string;
  last_seen_at: string;
}

/** A key the account signs viewer tokens with (dashboard only). */
export interface ViewerKey {
  object: 'viewer_key';
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

export interface ApiKey {
  id: string;
  name: string;
  redacted: string;
  created_at: string;
  last_used_at: string | null;
}

export const streamName = (s: Pick<Stream, 'id' | 'metadata'> & { name?: string | null }): string => {
  if (s.name) return s.name;
  const n = s.metadata?.name; // streams named before the name field existed
  return typeof n === 'string' && n.trim() ? n : s.id;
};

/**
 * Seconds until a pending stream's reservation runs out, counted from when the
 * response arrived (`fetchedAt`), so a wrong local clock doesn't matter. Null
 * when the stream isn't waiting.
 */
export function reservationSecondsLeft(s: Stream, fetchedAt: number, now: number): number | null {
  if (s.status !== 'pending') return null;
  const deadline =
    s.reservation_expires_in_seconds != null
      ? fetchedAt + s.reservation_expires_in_seconds * 1000
      : s.reservation_expires_at
        ? new Date(s.reservation_expires_at).getTime()
        : null;
  return deadline === null ? null : Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** The status to show: a waiting stream whose countdown has reached zero is expired, even before the next refresh says so. */
export function shownStatus(s: Stream, secondsLeft: number | null): Stream['status'] {
  return s.status === 'pending' && secondsLeft === 0 ? 'expired' : s.status;
}

/**
 * The secrets of streams created in this browser tab, kept in memory only:
 * they are shown on the stream's page until the tab is closed or reloaded,
 * exactly like "shown once" promises.
 */
export const createdSecrets = new Map<string, Pick<CreatedStream, 'stream_key' | 'whip_url'>>();

/** `viewers` on GET /v1/streams/:id (claude/viewers-plan.md). */
export interface StreamViewers {
  /** Live estimate from the hosted player; null when nobody is on the player or the stream is over. */
  now: { watching: number; waiting: number; buffering_pct: number; measured_at: string; exact: boolean } | null;
  peak: number | null;
  peak_at: string | null;
  watch_minutes: number | null;
  /** Where peak and watch_minutes come from: the CDN logs (every player, exact) or the player estimate. */
  source: 'cdn' | 'player' | null;
  cdn_counted_until: string | null;
}

export interface ViewerSeries {
  object: 'viewer_series';
  stream_id: string;
  player: { minute: string; watching: number; waiting: number; buffering: number }[];
  cdn: { minute: string; viewers: number }[];
  /** Where the watch time came from (CDN logs), largest first, top 30. `??` = unknown. */
  countries?: { country: string; watch_minutes: number; share_pct: number }[];
}

/** Viewer chat on a stream (docs.justonair.com/chat). */
export interface ChatSettings {
  enabled: boolean;
  reactions: boolean;
  moderation_group: string | null;
  host_name: string | null;
  visible_after_end: boolean;
  builtin_word_filter: boolean;
}

export type ChatState = 'open' | 'paused' | 'read_only' | 'closed' | 'off';

/** A message as the owner sees it: flags and what is known about the sender, never the IP. */
export interface ChatOwnerMessage {
  id: string;
  seq: number;
  nickname: string;
  text: string;
  role: 'host' | 'moderator' | null;
  created_at: string;
  session_id: string | null;
  fingerprint: string | null;
  network: string | null;
  country: string | null;
  asn: string | null;
  deleted_at: string | null;
  deleted_by: 'owner' | 'moderator' | null;
  deleted_by_moderator: { id: string; name: string | null } | null;
  filtered: boolean;
  shadow: boolean;
  cleared: boolean;
  visible: boolean;
}

export interface ChatFeed {
  state: ChatState;
  slow_mode_seconds: number;
  effective_slow_mode_seconds: number;
  slow_mode_auto: boolean;
  paused: boolean;
  pinned_message_id: string | null;
  cleared_at: string | null;
  moderation_group: string | null;
  busy: boolean;
  messages: ChatOwnerMessage[];
  deleted_ids: string[];
  next_after: number;
  has_more: boolean;
  reactions: { recent: Record<string, number>; total: Record<string, number> };
  totals: { messages_posted: number; messages_shown: number };
}

export interface ChatBan {
  id: string;
  scope: 'stream' | 'group';
  moderation_group: string | null;
  session_id: string | null;
  fingerprint: string | null;
  nickname: string | null;
  created_by: 'owner' | 'moderator' | null;
  created_by_moderator: { id: string; name: string | null } | null;
  created_at: string;
}

export interface ChatModerator {
  id: string;
  name: string;
  scope: 'stream' | 'group';
  moderation_group: string | null;
  status: 'invited' | 'active' | 'expired' | 'revoked';
  invite_expires_at: string | null;
  /** How long they moderate after opening the link (1-30 days). */
  active_days: number;
  /** When they stop: active_days after opening the link. */
  active_until: string | null;
  last_seen_at: string | null;
  created_at: string;
  /** Only in the answer to creating one: the single-use link. */
  invite_url?: string;
}

export interface ChatWords {
  scope: 'stream' | 'group';
  moderation_group: string | null;
  words: string[];
  builtin_word_filter: boolean;
}
